import React from "react";
import Loader from "components/Loader";
import { useUser } from "context/UserContext";
import { Col, Row, Card, Badge, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBullseye, faFileText, faStar, faDownload, faSearch, faChartLine, faShareAlt } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { css, StyleSheet } from "aphrodite";

function Home() {
  const { loading } = useUser();
  if (loading) return <Loader />;

  const dashboardData = [
    {
      id: "content_editor",
      title: "Content Editor",
      description: "AI-powered tools to create, edit, and optimize your content",
      icon: faFileText,
      color: "#e0e1dd",
      tag: "MOST USED",
      tagColor: "primary",
      subItems: [
        {
          title: "Content Journey",
          icon: faFileText,
          items: [
            { name: "Keyword", link: "/content_editor/content_journey/keyword" },
            { name: "URL", link: "/content_editor/content_journey/url" }
          ]
        },
        {
          title: "Headline Generation",
          icon: faFileText,
          items: [
            { name: "Keyword", link: "/content_editor/headlines/keyword" },
            { name: "URL", link: "/content_editor/headlines/url" }
          ]
        },
        {
          title: "Content Creation + Optimiser",
          icon: faBullseye,
          items: [
            { name: "URL-Based Optimisation", link: "/content_editor/writer/url" },
            { name: "Keyword-Based Optimisation", link: "/content_editor/writer/keyword" },
            { name: "Content-Based Optimisation", link: "/content_editor/writer/content" }
          ]
        },
        {
          title: "Keyword Discovery",
          icon: faSearch,
          items: [
            { name: "Keyword", link: "/content_editor/keyword_discovery/keyword" },
            { name: "URL", link: "/content_editor/keyword_discovery/url" }
          ]
        },
        {
          title: "AI Overview",
          icon: faStar,
          items: [
            { name: "Keyword", link: "/content_editor/ai_overview/keyword" },
            { name: "URL", link: "/content_editor/ai_overview/url" },
            { name: "Content", link: "/content_editor/ai_overview/content" }
          ]
        },
        {
          title: "Import Content",
          icon: faDownload,
          items: [
            { name: "Content", link: "/content_editor/import_content/content" }
          ]
        }
      ]
    },
    {
      id: "seo",
      title: "SEO",
      description: "Comprehensive SEO tools for keyword research, optimization, and ranking analysis",
      icon: faChartLine,
      color: "#dad7cd",
      tag: "NEW",
      tagColor: "warning",
      subItems: [
        {
          title: "Keyword Research",
          icon: faSearch,
          items: [
            { name: "Keyword", link: "/seo/keyword_research/keyword" },
            { name: "URL", link: "/seo/keyword_research/url" }
          ]
        },
        {
          title: "SEO Title",
          icon: faFileText,
          items: [
            { name: "Single Keyword", link: "/seo/title/keyword" },
            { name: "URL", link: "/seo/title/url", disabled: true }
          ]
        },
        {
          title: "SEO Meta Description",
          icon: faFileText,
          items: [
            { name: "Single Keyword", link: "/seo/meta_description/keyword" },
            { name: "URL", link: "/seo/meta_description/url", disabled: true }
          ]
        },
        {
          title: "Ranking",
          icon: faChartLine,
          items: [
            { name: "Keyword", link: "/seo/ranking/keyword" },
            { name: "Domain Ranking", link: "/seo/ranking/domain_ranking", disabled: true },
            { name: "Page Ranking", link: "/seo/ranking/page_ranking", disabled: true }
          ]
        },
        {
          title: "Image ALT Tags",
          icon: faFileText,
          items: [
            { name: "Keyword Optimisation", link: "/seo/image/keyword_optimisation" },
            { name: "ALT Tag Optimisation", link: "/seo/image/alt_tag_optimisation", disabled: true }
          ]
        }
      ]
    },
    {
      id: "social",
      title: "Social",
      description: "Create and optimize social media content for maximum engagement",
      icon: faShareAlt,
      color: "#fefae0",
      tag: "PRO",
      tagColor: "info",
      subItems: [
        {
          title: "Meta + Instagram",
          icon: faShareAlt,
          items: [
            { name: "Tags", link: "/social/meta_insta/tags" },
            { name: "Content Creation", link: "/social/meta_insta/content_creation" }
          ]
        }
      ]
    }
  ];

  return (
    <div className={classNames("container px-4", css(styles.container))}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 className="mb-0">BuddyX Your AI Writer</h1>
        </div>
      </div>

      <Row className="g-3 g-md-4 g-lg-3">
        {dashboardData.map((section, index) => (
          <Col 
            key={section.id} 
            xs={12}
            sm={12}
            md={12}
            lg={index === 0 ? 8 : 4}
            xl={index === 0 ? 8 : 4}
            className={classNames(
              index === 2 ? "d-flex align-items-start" : "",
              index === 2 ? css(styles.socialCardContainer) : ""
            )}
          >
            <Card 
              className={classNames(
                css(index === 2 ? styles.socialCard : styles.dashboardCard),
                css(styles.cardSpacing)
              )}
              style={{ backgroundColor: section.color }}
            >
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon 
                      icon={section.icon} 
                      className="me-3 text-primary" 
                      style={{ fontSize: "1.5rem" }}
                    />
                    <div>
                      <h4 className="mb-1">{section.title}</h4>
                      <p className="text-muted mb-0 small">{section.description}</p>
                    </div>
                  </div>
                  <Badge bg={section.tagColor} className="ms-2">
                    {section.tag}
                  </Badge>
                </div>

                <div className="mb-2">
                  <Row className="g-2">
                    {section.subItems.map((subItem, subIndex) => (
                      <Col key={subIndex} xs={12} sm={12} md={6} lg={6} xl={6}>
                        <Card className="border-0 bg-white shadow-sm">
                          <Card.Body className="p-2">
                            <div className="d-flex align-items-center mb-1">
                              <FontAwesomeIcon 
                                icon={subItem.icon} 
                                className="me-2 text-primary" 
                                style={{ fontSize: "0.9rem" }}
                              />
                              <h6 className="mb-0">{subItem.title}</h6>
                            </div>
                            <div className="d-flex flex-wrap gap-1">
                              {subItem.items.map((item, itemIndex) => (
                                <Button
                                  key={itemIndex}
                                  variant={item.disabled ? "outline-secondary" : "outline-primary"}
                                  size="sm"
                                  className={classNames(
                                    "me-1 mb-1",
                                    item.disabled && css(styles.disabledButton),
                                    css(styles.responsiveButton)
                                  )}
                                  disabled={item.disabled}
                                  href={item.disabled ? undefined : item.link}
                                  style={{
                                    fontSize: "0.75rem",
                                    opacity: item.disabled ? 0.6 : 1
                                  }}
                                >
                                  <span className="text-truncate">
                                    {item.name}
                                    {item.disabled && (
                                      <small className="ms-1 text-muted">(Soon)</small>
                                    )}
                                  </span>
                                </Button>
                              ))}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    maxWidth: "1500px",
  },
  dashboardCard: {
    borderRadius: "12px",
    border: "none",
    boxShadow: "none",
    transition: "all 0.3s ease",
    height: "fit-content",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "none",
    },
  },
  socialCard: {
    borderRadius: "12px",
    border: "none",
    boxShadow: "none",
    transition: "all 0.3s ease",
    height: "fit-content",
    position: "relative",
    zIndex: 1,
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "none",
    },
  },
  disabledButton: {
    cursor: "not-allowed",
    opacity: 0.6,
  },
  socialCardContainer: {
    marginTop: "-7.5%",
    marginBottom: "1rem",
    "@media (max-width: 1199px)": {
      marginTop: "0",
      marginBottom: "1rem",
    },
    "@media (max-width: 991px)": {
      marginTop: "0",
      marginBottom: "1rem",
    },
  },
  cardSpacing: {
    marginBottom: "1.5rem",
    "@media (max-width: 767px)": {
      marginBottom: "2rem",
    },
  },
  responsiveButton: {
    maxWidth: "100%",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    "@media (max-width: 576px)": {
      fontSize: "0.7rem !important",
      padding: "0.25rem 0.5rem",
    },
  },
});

export default function Page() {
  return <Home />;
}
